import React, {
    useContext, useCallback, useEffect, useState
} from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { ModalNextProvider, Loader } from '@jutro/components';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import { Link } from 'gw-components-platform-react';
import { MetadataForm } from '@jutro/uiconfig';
import { error } from '@jutro/logger';
import { TermsAndConditionUtils } from 'gw-portals-util-js';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { withApplicationPropertyContext, PaymentComponent } from 'tdic-components-platform-react';
import ChangePaymentPlanComponent from '../components/ChangePaymentPlan/ChangePaymentPlan';
import ChangeAutopayBankAccountComponent from '../components/ChangeAutopayBankAccount/ChangeAutopayBankAccount';
import BillingDocuments from '../components/BillingDocuments/BillingDocuments';
import metadata from './BillingSummary.metadata.json5';
import styles from './BillingSummary.module.scss';
import messages from './BillingSummary.messages';

const getAnyPaidInvoices = (invoiceSummary) => {
    return _.filter(invoiceSummary, (item) => {
        const { amount } = item.totalPaid;
        return item.paidStatus === 'fullypaid' || amount > 0;
    });
};

const getUnpaidInvoices = (invoiceSummary) => {
    return _.filter(invoiceSummary, (item) => item.paidStatus !== 'fullypaid');
};

const hasUnpaidInvoices = (invoiceSummary) => {
    const unpaidInvoices = getUnpaidInvoices(invoiceSummary);
    return unpaidInvoices.length > 0;
};

const getCell = (item, index, property) => {
    return item[property.id];
};

function BillingSummary(props) {
    const { tdic: { paymentPlanCode } } = appConfig;

    const {
        invoiceStreamId,
        accountLevelBilling,
        lobCode,
        paymentGroupDetails,
        currency,
        authHeader,
        reloadInvoiceDetails,
        showManagePayment,
        paymentInstrumentPlan,
        tabResult,
        callbackToGetPaymentPlan,
        tncTransactionTypesSubTypes,
        callbackToGetBillingDocument,
        documentDetails,
        user_name: userName,
        email,
        isAuthorisedUser,
        termData: terms
    } = props;
    const translator = useContext(TranslatorContext);

    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );

    // screen states
    const [isLoadingData, updateIsLoadingData] = useState(false);
    const [selectedInvoicesForPayment, updateSelectedInvoicesForPayment] = useState([]);
    const [selectedInvoiceFilter, updateSelectedInvoiceFilter] = useState('planned');
    const [selectedScreen, updateSelectedScreen] = useState('default');
    const [manualAmountToPay, updateManualAmount] = useState(null);
    const [currentPaymentPlanSelected,
        updateCurrentPaymentPlanSelected] = useState(paymentInstrumentPlan);
    const [bankAccountPaymentInstrument, setBankAccountPaymentInstrument] = useState({});
    const [showPaymentMethod, updatePaymentMethod] = useState(false);

    const {
        invoiceSummary_Ext: invoiceSummary = [],
        paymentInstrument,
        paymentInstrumentSummary,
        periodicity,
        cancellationStatus,
        isExpired,
        canChangePaymentPlan
    } = paymentGroupDetails;

    const resetInternalStates = useCallback(() => {
        updateIsLoadingData(true);
        reloadInvoiceDetails(invoiceStreamId, true, callbackToGetPaymentPlan).finally(() => {
            updateSelectedInvoicesForPayment([]);
            updateSelectedInvoiceFilter('planned');
            updateManualAmount(null);
            updateIsLoadingData(false);
        });
    }, [reloadInvoiceDetails, invoiceStreamId, callbackToGetPaymentPlan]);

    const getInvoiceStreamHeader = useCallback((isAccLevelStream, paymentGroup) => {
        let header = '';
        const isAutomaticPaymentEnabled = paymentInstrumentPlan.paymentPlan
        === paymentPlanCode.monthly;
        if (isAccLevelStream) {
            header = isAutomaticPaymentEnabled
                ? messages.invoicesAutomaticPaymentsEnabled
                : messages.invoices;
        } else {
            return `${paymentGroup.offeringName} ${_.get(tabResult, 'policyNumber')} ${isAutomaticPaymentEnabled ? translator(messages.automaticPaymentsEnabled) : ''}`;
        }
        return translator(header, { policyNumber: _.get(tabResult, 'policyNumber') });
    }, [paymentInstrumentPlan, paymentPlanCode, tabResult, translator]);

    const invokeTnCCall = useCallback(() => {
        TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''),
            tncTransactionTypesSubTypes, 'ChangePaymentPlan', 'ChangePaymentPlan',
            authHeader, _.get(tabResult, 'policyNumber'), userName, email);
    }, [authHeader, email, tabResult, terms, tncTransactionTypesSubTypes, userName]);

    const onClickConfirmSetupAutomaticPayment = useCallback(
        (paymentDetailsVm, paymentMethod, selectedPaymentPlan) => {
            updateCurrentPaymentPlanSelected(selectedPaymentPlan);
            const paymentDetailsValue = paymentDetailsVm.value;
            const PaymentPlan = {
                policyNumber: _.get(tabResult, 'policyNumber'),
                PaymentPlan: selectedPaymentPlan
            };
            const PaymentInstrument = {
                PaymentMethod: paymentMethod,
                BankAccountData: {
                    ...paymentDetailsValue
                }
            };
            const requestPayload = selectedPaymentPlan === paymentPlanCode.monthly
                ? { PaymentPlan, PaymentInstrument } : { PaymentPlan };
            BillingService.changePaymentPlanAndCreateInstrument(requestPayload,
                authHeader)
                .then((reponse) => {
                    if (reponse === true) {
                        invokeTnCCall();
                        updatePaymentMethod(reponse);
                        updateSelectedScreen('automatic-payment-plan-confirmation');
                    }
                })
                .catch((err) => {
                    error(`BillingSummary: finalLoad - Unable to set automatic payment ${JSON.stringify(err)}`);
                    ModalNextProvider.showAlert({
                        title: messages.accountUpdateFailed,
                        message: messages.sorryWeWereNotAbleToSetupAutomaticPaymentsOnYourAccount,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                });
        },
        [tabResult, paymentPlanCode, BillingService, authHeader,
            invokeTnCCall, resetInternalStates]
    );

    const onClickCancelSetupAutomaticPayment = useCallback(() => {
        updateSelectedScreen('default');
    }, []);

    const onClickConfirmChangeBankAccount = useCallback(
        (paymentDetailsVm, paymentMethod) => {
            const paymentDetailsValue = paymentDetailsVm.value;
            const { policyNumber } = tabResult;
            const paymentMethodPayload = {
                PaymentMethod: paymentMethod,
                BankAccountData: {
                    ...paymentDetailsValue
                }
            };

            BillingService.changePaymentMethod(
                policyNumber,
                paymentMethodPayload,
                authHeader
            )
                .then(() => {
                    updateSelectedScreen('bank-payment-source-confirmation');
                })
                .catch((err) => {
                    error(`BillingSummary: onClickConfirmChangeBankAccount - Unable to change payment method ${JSON.stringify(err)}`);
                    ModalNextProvider.showAlert({
                        title: messages.changingPaymentMethod,
                        message: messages.thereWasAProblemChangingThePaymentMethod,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                });
        },
        [tabResult, BillingService, authHeader, resetInternalStates]
    );

    const onClickCancelChangeBankAccount = useCallback(() => {
        updateSelectedScreen('default');
    }, []);

    const onUpdateSelectedInvoices = useCallback(
        (selectedInvoiceSummariesId) => {
            const selectedInvoiceObjects = _.filter(invoiceSummary, (anInvoiceSummary) => {
                return selectedInvoiceSummariesId.includes(anInvoiceSummary.invoiceId);
            });

            const selectedTotalAmount = _.sumBy(selectedInvoiceObjects, (invoice) => {
                return Number(_.get(invoice, 'amountDue.amount'));
            }).toFixed(2);
            const amountObject = {
                amount: selectedTotalAmount,
                currency: currency
            };

            updateManualAmount(amountObject);
            updateSelectedInvoicesForPayment(selectedInvoiceSummariesId);
        },
        [invoiceSummary, currency]
    );

    const getBankAccountPaymentInstrument = useCallback(() => {
        const { policyNumber } = tabResult;
        BillingService.getPaymentInstrument(
            policyNumber,
            authHeader
        ).then((result) => {
            setBankAccountPaymentInstrument(result);
        });
    }, [BillingService, authHeader, tabResult]);

    const handleMakeAPayment = useCallback(() => {
        const { history } = props;
        return history.push('/make-payment');
    }, [props]);

    const onClickMenuLink = useCallback(
        (evt) => {
            const { id: menuLinkId } = evt.currentTarget;
            let screen;
            switch (menuLinkId) {
                case 'makeAPaymentLinkId':
                    screen = 'payment';
                    handleMakeAPayment();
                    break;
                case 'setupAutomaticPaymentsLinkId':
                    callbackToGetPaymentPlan(_.get(tabResult, 'policyNumber'));
                    screen = 'automatic-payment-plan';
                    break;
                case 'changeAutoPayBankAccountLinkId':
                    screen = 'bank-payment-source';
                    getBankAccountPaymentInstrument();
                    break;
                case 'viewInvoicesLinkId':
                    screen = 'default';
                    break;
                case 'billingDocumentsLink':
                    screen = 'billing-document';
                    callbackToGetBillingDocument(_.get(tabResult, 'policyNumber'));
                    break;
                default:
                    screen = 'default';
                    resetInternalStates();
                    break;
            }
            updateSelectedScreen(screen);
        },
        [handleMakeAPayment, callbackToGetPaymentPlan, tabResult,
            getBankAccountPaymentInstrument, callbackToGetBillingDocument, resetInternalStates]
    );

    const onClickFilterChange = useCallback((value) => {
        updateSelectedInvoiceFilter(value);
    }, []);

    const monthlyApwPay = useCallback(() => {
        const checkPaymentPlan = currentPaymentPlanSelected === paymentPlanCode.monthly
            ? translator(messages.payment)
            : currentPaymentPlanSelected;
        const monthlyPayText = currentPaymentPlanSelected === paymentPlanCode.monthly
            ? messages.automaticPaymentPlan
            : messages.semiAnnualAcknowledgementMessage;
        return (
            <FormattedMessage
                {...monthlyPayText}
                values={{
                    policyNumber: _.get(tabResult, 'policyNumber'),
                    paymentData: checkPaymentPlan
                }}
            />
        );
    }, [currentPaymentPlanSelected, paymentPlanCode.monthly, tabResult, translator]);

    const isVisibleChangeAutoPayBankAccountLink = useCallback(() => {
        return (!!paymentInstrumentSummary
            || paymentInstrumentPlan.paymentPlan === paymentPlanCode.monthly)
            && (cancellationStatus !== 'canceled')
            && !isExpired
            && !isAuthorisedUser
            && canChangePaymentPlan
            && !/-/gi.test(_.get(tabResult, 'displayName', ''));
    }, [canChangePaymentPlan, cancellationStatus, isExpired, isAuthorisedUser,
        paymentInstrumentPlan, paymentInstrumentSummary, paymentPlanCode, tabResult]);

    // Once payment mode set it as automatic,
    // we are hiding menu once user selects ok button we are retrieving data
    const isVisibleMenuLink = useCallback(() => {
        return (invoiceStreamId || accountLevelBilling)
        && !(selectedScreen === 'automatic-payment-plan-confirmation');
    }, [accountLevelBilling, invoiceStreamId, selectedScreen]);

    const isVisibleSetupAutomaticPaymentLink = useCallback(() => {
        return !paymentInstrumentSummary
            && (cancellationStatus !== 'canceled')
            && !isExpired
            && !isAuthorisedUser
            && canChangePaymentPlan
            && !/-/gi.test(_.get(tabResult, 'displayName', ''));
    }, [canChangePaymentPlan, cancellationStatus, isExpired, isAuthorisedUser,
        paymentInstrumentSummary, tabResult]);

    useEffect(() => {
        const defaultFilter = hasUnpaidInvoices(invoiceSummary) ? 'planned' : 'paid';
        updateSelectedInvoiceFilter(defaultFilter);
        if (showManagePayment) {
            updateSelectedScreen('automatic-payment-plan');
        }
    }, [invoiceSummary, showManagePayment]);

    const getEquityPercentageMsg = useCallback(() => {
        return (
            <FormattedMessage
                {...messages.equityPercentageMsg}
                values={{
                    link: <Link to="/make-payment">{translator(messages.payMyBillLink)}</Link>
                }}
            />
        );
    }, [translator]);

    if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        menuLinkContainerId: {
            visible: isVisibleMenuLink()
        },
        makeAPaymentLinkId: {
            visible: hasUnpaidInvoices(invoiceSummary) || cancellationStatus === 'canceled'
        },
        cancelledPolicyContent: {
            visible: cancellationStatus === 'canceled' || isExpired
        },
        equityPercentageContent: {
            visible: !canChangePaymentPlan && cancellationStatus !== 'canceled' && !isExpired,
            content: getEquityPercentageMsg()
        },
        billingDocText: {
            visible: !_.isEmpty(documentDetails)
        },
        changeAutoPayBankAccountLinkId: {
            visible: isVisibleChangeAutoPayBankAccountLink()
        },
        setupAutomaticPaymentsLinkId: {
            visible: isVisibleSetupAutomaticPaymentLink()
        },
        billingInvoicesContainerId: {
            visible: selectedScreen === 'default' && !!invoiceStreamId
        },
        billingInvoicesHeaderId: {
            content: getInvoiceStreamHeader(accountLevelBilling, paymentGroupDetails, lobCode),
            visible: invoiceStreamId || accountLevelBilling
        },
        changePaymentMethodId: {
            visible: paymentInstrumentPlan.paymentPlan === paymentPlanCode.monthly
        },
        invoicesFilterToggleId: {
            onValueChange: onClickFilterChange
        },
        billingInvoicesComponentId: {
            invoiceSummary:
                selectedInvoiceFilter === 'planned'
                    ? getUnpaidInvoices(invoiceSummary)
                    : getAnyPaidInvoices(invoiceSummary),
            showPaidDate: selectedInvoiceFilter === 'paid'
        },
        makePaymentContainerId: {
            visible: selectedScreen === 'payment'
        },
        makePaymentInvoicesTableComponentId: {
            invoiceSummary: getUnpaidInvoices(invoiceSummary),
            selectedInvoices: selectedInvoicesForPayment,
            onUpdateSelectedInvoices: onUpdateSelectedInvoices
        },
        makePaymentAmountToPayId: {
            defaultCurrency: currency,
            disabled: _.isEmpty(selectedInvoicesForPayment),
            onValueChange: updateManualAmount
        },
        setUpAutomaticPaymentConfirmationMessageId: {
            visible: !showPaymentMethod
        },
        setUpAutomaticPayment: {
            visible: showPaymentMethod,
            content: monthlyApwPay()
        },
        setUpAutomaticPaymentComponentId: {
            visible: selectedScreen === 'automatic-payment-plan',
            model: paymentInstrument,
            prevPaymentInstrumentPlan: paymentInstrumentPlan,
            callbackConfirmSetupAutomaticPayment: onClickConfirmSetupAutomaticPayment,
            callbackCancelSetupAutomaticPayment: onClickCancelSetupAutomaticPayment,
            periodicity: periodicity,
            cancellationStatus,
            isExpired,
            canChangePaymentPlan
        },
        changeBankAccountComponentId: {
            xCenter: 'bc',
            model: paymentInstrument || {},
            callbackConfirmChangeBankAccount: onClickConfirmChangeBankAccount,
            callbackCancelChangeBankAccount: onClickCancelChangeBankAccount,
            bankAccountPaymentInstrument: bankAccountPaymentInstrument || {}
        },
        setUpAutomaticPaymentConfirmationContainerId: {
            visible: selectedScreen === 'automatic-payment-plan-confirmation'
        },
        setUpAutomaticPaymentConfirmationInfoId: {
            visible: !showPaymentMethod,
            content: translator(messages.yourAutomaticPaymentsWillBeginOn, {
            })
        },
        changeBankAccountContainerId: {
            visible: selectedScreen === 'bank-payment-source'
        },
        changeBankAccountConfirmationContainerId: {
            visible: selectedScreen === 'bank-payment-source-confirmation'
        },
        billingDocumentsComponent: {
            visible: selectedScreen === 'billing-document',
            data: documentDetails
        }
    };

    const dataForComponent = {
        invoiceStreamId,
        selectedInvoiceFilter,
        periodicity,
        cancellationStatus,
        manualAmountToPay
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            paymentpagecomponent: PaymentComponent,
            changepaymentplancomponent: ChangePaymentPlanComponent,
            changeautopaybankaccountcomponent: ChangeAutopayBankAccountComponent,
            billingdocumentscomponent: BillingDocuments
        },
        resolveCallbackMap: {
            onClickMenuLink,
            onClickConfirmSetupAutomaticPayment,
            onClickCancelSetupAutomaticPayment,
            onClickConfirmChangeBankAccount,
            onClickCancelChangeBankAccount,
            getCell
        }
    };

    return (
        <MetadataForm
            data={dataForComponent}
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

BillingSummary.propTypes = {
    invoiceStreamId: PropTypes.string,
    callbackToGetPaymentPlan: PropTypes.func.isRequired,
    accountLevelBilling: PropTypes.bool,
    lobCode: PropTypes.string,
    paymentGroupDetails: PropTypes.shape({
        invoiceSummary_Ext: PropTypes.array,
        paymentInstrument: PropTypes.object,
        paymentInstrumentSummary: PropTypes.object,
        periodicity: PropTypes.string,
        cancellationStatus: PropTypes.string,
        isExpired: PropTypes.bool,
        canChangePaymentPlan: PropTypes.bool
    }),
    currency: PropTypes.string.isRequired,
    authHeader: PropTypes.shape({}).isRequired,
    reloadInvoiceDetails: PropTypes.func.isRequired,
    showManagePayment: PropTypes.bool,
    paymentInstrumentPlan: PropTypes.shape({
        paymentPlan: PropTypes.string,
    }).isRequired,
    tabResult: PropTypes.shape({
        policyNumber: PropTypes.string
    }).isRequired,
    tncTransactionTypesSubTypes: PropTypes.shape({}).isRequired,
    user_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    isAuthorisedUser: PropTypes.bool.isRequired,
    documentDetails: PropTypes.arrayOf(PropTypes.object),
    callbackToGetBillingDocument: PropTypes.func.isRequired,
    termData: PropTypes.shape({}).isRequired
};

BillingSummary.defaultProps = {
    invoiceStreamId: null,
    accountLevelBilling: null,
    lobCode: null,
    paymentGroupDetails: {
        invoiceSummary_Ext: [],
        paymentInstrument: null,
        paymentInstrumentSummary: null,
        periodicity: null,
        cancellationStatus: null,
        isExpired: false,
        canChangePaymentPlan: false
    },
    showManagePayment: false,
    documentDetails: []
};

export default
withRouter(withAuthenticationContext(withApplicationPropertyContext(BillingSummary)));
